import { httpQuery } from '@luminovo/data-stack';
import { searchParam, useSetSearch } from '@luminovo/design-system';
import { createFileRoute, notFound } from '@tanstack/react-router';
import type { JSX } from 'react';
import { z } from 'zod/v4';

import AssemblyDetailsPage from '@/areas/assemblies/BomItems/AssemblyDetailsPage';
import { bomTabs } from '@/areas/assemblies/common/bomDetailsQueryParams';
import { assembliesModulePermissions } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// An assembly the API doesn't know renders the not-found page. The loader asks with the query the page reads it with.
export const Route = createFileRoute('/_authenticated/assemblies/$assemblyId/details')({
    staticData: {
        access: { permissions: ['view:assembly'], modulePermissions: assembliesModulePermissions },
        chrome: 'home',
    } satisfies PageStaticData,
    validateSearch: z.object({
        designItemId: searchParam.list('optional'),
        filters: z.string().optional(),
        bomTab: searchParam.enum(bomTabs, 'optional'),
        dashboardFilters: z.string().optional(),
        search: z.string().optional(),
    }),
    loader: async ({ context: { queryClient }, params: { assemblyId } }) => {
        const { items } = await queryClient.ensureQueryData(
            httpQuery('POST /assemblies/bulk', {
                requestBody: { ids: [assemblyId] },
                queryParams: { with_aggregations: false },
            }),
        );
        if (items.length === 0) {
            throw notFound();
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const pathParams = Route.useParams();
    const designItemId = Route.useSearch({ select: (search) => search.designItemId });
    const filters = Route.useSearch({ select: (search) => search.filters });
    const bomTab = Route.useSearch({ select: (search) => search.bomTab });
    const dashboardFilters = Route.useSearch({ select: (search) => search.dashboardFilters });
    const searchText = Route.useSearch({ select: (search) => search.search });
    const setSearch = useSetSearch();
    return (
        <AssemblyDetailsPage
            {...pathParams}
            queryParams={{
                designItemId,
                filters,
                bomTab,
                dashboardFilters,
                search: searchText,
            }}
            onSearchChange={setSearch}
        />
    );
}
